import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { CurrencyProvider } from "@/context/CurrencyContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PatientDashboard from "@/pages/PatientDashboard";
import TestCatalog from "@/pages/TestCatalog";
import TestDetails from "@/pages/TestDetails";
import BookingPage from "@/pages/BookingPage";
import MyBookings from "@/pages/MyBookings";
import MedicalRecords from "@/pages/MedicalRecords";
import LabResults from "@/pages/LabResults";
import ProfilePage from "@/pages/ProfilePage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminDashboard from "@/pages/AdminDashboard";
import WalletPage from "@/pages/WalletPage";
import SettingsPage from "@/pages/SettingsPage";
import ResultEntryPage from "@/pages/ResultEntryPage";
import LabTemplatesPage from "@/pages/LabTemplatesPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminPaymentsPage from "@/pages/AdminPaymentsPage";
import AdminInventoryPage from "@/pages/AdminInventoryPage";
import AdminTestsPage from "@/pages/AdminTestsPage";
import ReceptionistPage from "@/pages/ReceptionistPage";
import AboutPage from "@/pages/AboutPage";
import CareersPage from "@/pages/CareersPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import BookingPaymentSummary from "@/pages/BookingPaymentSummary";
import AIChatWidget from "@/components/AIChatWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Seed database on first load
const seedDatabase = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/seed`, { method: 'POST' });
  } catch (e) {
    console.log('Seed may already exist or failed');
  }
};

function AppRoutes() {
  const { user } = useAuth();

  useEffect(() => {
    seedDatabase();
  }, []);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />} />
      <Route path="/tests" element={<TestCatalog />} />
      <Route path="/tests/:testId" element={<TestDetails />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          {user?.role === 'admin' ? <AdminDashboard /> : <PatientDashboard />}
        </ProtectedRoute>
      } />
      <Route path="/book/:testId" element={
        <ProtectedRoute>
          <BookingPage />
        </ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute>
          <MyBookings />
        </ProtectedRoute>
      } />
      <Route path="/bookings/:bookingId" element={
        <ProtectedRoute>
          <MyBookings />
        </ProtectedRoute>
      } />
      <Route path="/medical-records" element={
        <ProtectedRoute>
          <MedicalRecords />
        </ProtectedRoute>
      } />
      <Route path="/results" element={
        <ProtectedRoute>
          <LabResults />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          <WalletPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/payment-success" element={
        <ProtectedRoute>
          <PaymentSuccess />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/result-entry" element={
        <ProtectedRoute allowedRoles={['admin', 'lab_tech', 'lab_scientist']}>
          <ResultEntryPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/templates" element={
        <ProtectedRoute allowedRoles={['admin', 'lab_scientist']}>
          <LabTemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute allowedRoles={['admin', 'receptionist']}>
          <AdminPaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/inventory" element={
        <ProtectedRoute allowedRoles={['admin', 'lab_tech', 'lab_scientist']}>
          <AdminInventoryPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/tests" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminTestsPage />
        </ProtectedRoute>
      } />
      
      {/* Receptionist Routes */}
      <Route path="/receptionist" element={
        <ProtectedRoute allowedRoles={['admin', 'receptionist']}>
          <ReceptionistPage />
        </ProtectedRoute>
      } />
      
      {/* Payment Summary */}
      <Route path="/payment/:bookingId" element={
        <ProtectedRoute>
          <BookingPaymentSummary />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Wrapper component for AI Chat to check auth
function AppWithChat() {
  const { user } = useAuth();
  return (
    <>
      <AppRoutes />
      <Toaster position="top-right" richColors />
      {user && <AIChatWidget />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <CurrencyProvider>
            <AuthProvider>
              <AppWithChat />
            </AuthProvider>
          </CurrencyProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
